import axios from 'axios';
import { getApiBaseUrl } from './api';
import { askConnectFields } from './connectFields';
import type { ConnectField } from './connectFields';
import type {
  CodeHostStatus,
  McpAddBody, McpCatalogEntry, McpServer, McpView, McpWriteLevel, MyMcpView, PersonalMcpServer,
  FrameworkReadTarget,
  MyPart,
  IntakeSection,
  IntakeSectionBody,
  IntakeSectionsView,
  IntakeJourney,
  IntakePersonDetail,
  IntakePersonProgress,
  ClientPerson,
  AaiOpportunity,
  AaiContext,
  AccessMine,
  ActivityEvent,
  ActivityFilters,
  ActivityFeed,
  MemberPreferences,
  MyPreferencesResponse,
  MyResults,
  PendingReview,
  QuickWinList,
  AaiDepartment,
  AaiHome,
  AaiNotificationList,
  AaiRequest,
  ToolControl,
  ProjectDocuments,
  CompanyDocuments,
  CompanyDocument,
  CompanyDocumentLevel,
  CompanyDocumentLibrary,
  AaiRoutine,
  AaiSession,
  AaiSkill,
  SkillAccessRow,
  ToolOpportunity,
  ToolOpportunityList,
  SkillUsage,
  VendorEvaluation,
  VendorEntry,
  VendorResearch,
  Cohort,
  CohortDetail,
  CohortSuggestion,
  CompanyDashboard,
  AaiPolicy,
  SkillPrefill,
  PersonalSkill,
  SkillRun,
  VettingData,
  Graduation,
  OrgChartData,
  Mentorship,
  TeamBundle,
  Certification,
  ImpactData,
  ToolLibraryEntry,
  ToolDive,
  ToolDelegation,
  CompanyToolPolicy,
  CompanyActionLevel,
  ConnectorCatalog,
  McpPermissionsView,
  McpToolDecision,
  AppAccessMode,
  ConnectorScope,
  ConnectorStatus,
  PermissionProfile,
  DiveStep,
  DiveTrackerData,
  DiveTurnResult,
  Curriculum,
  DashboardTrend,
  DepartmentAggregate,
  DiscoveredTool,
  GettingStarted,
  GovernanceData,
  HelpData,
  MySessions,
  MyTraining,
  Quiz,
  QuizResult,
  MyToolsData,
  OnboardingAnswer,
  OnboardingState,
  PrdReviewDetail,
  PrdReviewList,
  PrdReviewState,
  ProductivityData,
  ProgramConfig,
  ProgramTemplate,
  ReadinessResponse,
  RegistryRow,
  ReportsData,
  ScoringConfig,
  RosterResponse,
  TransparencyNotice,
  VoiceConnection,
  VideoLibrary,
  AaiVideo,
  WhatsAllowedData,
  Workspace,
  OrbitCollection,
  OrbitCollectionView,
  OrbitRoomView,
  GrantsView,
  OrbitRoomSummary,
  OrbitRoomPerson,
  OrbitAsk,
  OrbitDeckResponse,
  OrbitJourney,
  OrbitToolBoard,
  OrbitOverview,
  OrbitProposal,
  FrameworkDocumentRead,
  FrameworkReadJob,
  FrameworkStoredDocument,
  PersonIntakeLink,
  AppliedEngagement,
  ArcPhase,
  EngagementBoard,
  EngagementTemplate,
  PartnerJourneyDefault,
  FrameworkProfile,
  FrameworkGap,
  ProposedArc,
  OrbitConversation,
  PersonIntakeQuestions,
  PersonIntakeState,
  PersonProspect,
  ProspectCompany,
  WorkspaceDocument,
  FrameworkValueProposal,
  JourneyHint,
  FrameworkView,
  PartnerFramework,
  IntakeTypeRow,
  StarterDeckCard,
  DeliveryNote,
  DeliveryProjectPanel,
  DeliveryProject,
  DeliveryQuote,
  PlanAndUsageData,
  PartnerSettingsData,
  PartnerOverview,
  OwnerOverview,
  OrganizationConnections,
  AiControl,
  OwnerControls, OwnerAdoption,
  ControlEvent,
  PortfolioRecommendation,
  PortfolioRecommendationList,
  DepartmentOverview,
} from '../types/aai';

interface ConnectFieldsResponse {
  fields: ConnectField[];
  setup: { scheme: string; fields: ConnectField[]; redirectUri: string; canSetUp: boolean } | null;
}

// Backend mounts the AAI routers at "/api/aai"; getApiBaseUrl() already ends in "/api".
//
// Company scope is derived server-side from the token by default. A CONSULTANT spans
// multiple companies, so they pick one with the switcher: `setAaiCompany(id)` stores it
// here and every scoped call sends `?company_id=` (re-authorized server-side — a
// consultant can only reach companies assigned to them). Client-side roles leave it null
// and the token's workspace is used.
let activeCompanyId: string | null = null;
export const setAaiCompany = (id: string | null) => { activeCompanyId = id; };

const base = () => `${getApiBaseUrl()}/aai`;
const scoped = () => (activeCompanyId ? { params: { company_id: activeCompanyId } } : undefined);
// The app shell reads the caller's own company even while a page is scoped to
// a client they serve (a provider Owner inside one of their clients).
const own = (ownCompany?: boolean) => (ownCompany ? undefined : scoped());

// Query params drop the keys a caller left empty, so the URL says only what was asked.
const compact = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all'));

export const aaiApi = {
  setAaiCompany,

  // Companies the caller can act on (consultant → their assigned clients).
  getWorkspaces: () =>
    axios.get<{ workspaces: Workspace[] }>(`${getApiBaseUrl()}/workspace/my-workspaces`),

  // Context that drives which tabs to show.
  getContext: (ownCompany?: boolean) => axios.get<AaiContext>(`${base()}/me`, own(ownCompany)),

  // Onboarding is self-service — identity comes from the token, no company param.
  getTransparency: () =>
    axios.get<{ notice: TransparencyNotice; acknowledged: boolean }>(`${base()}/onboarding/transparency`),
  acceptConsent: () => axios.post<{ ok: boolean; acknowledged: boolean }>(`${base()}/consent`, {}),
  getOnboardingState: () => axios.get<OnboardingState>(`${base()}/onboarding/state`),
  submitTurn: (body: { question_id?: string; value: OnboardingAnswer }) =>
    axios.post<OnboardingState>(`${base()}/onboarding/turn`, body),
  restartOnboarding: () => axios.post<OnboardingState>(`${base()}/onboarding/restart`, {}),
  scheduleOnboardingCall: (scheduledFor: string, toNumber: string) =>
    axios.post<{ ok: boolean; call_id: string; scheduled_for: string | null; to_number: string }>(
      `${base()}/onboarding/schedule`, { scheduled_for: scheduledFor, to_number: toNumber },
    ),
  cancelOnboardingCall: () =>
    axios.delete<{ ok: boolean; cancelled: number }>(`${base()}/onboarding/schedule`),

  // In-browser ElevenLabs voice onboarding (Option B). connection mints a signed
  // URL (503 → not configured, UI falls back); session logs the call for audit.
  getVoiceConnection: () => axios.get<VoiceConnection>(`${base()}/onboarding/voice/connection`),
  logVoiceSession: (body: { conversation_id?: string; status?: string; completed_by_voice?: boolean; transcript?: unknown; summary?: string; error?: string }) =>
    axios.post<{ ok: boolean }>(`${base()}/onboarding/voice/session`, body),

  // Readiness reads (company-scoped).
  getMyReadiness: () => axios.get<ReadinessResponse>(`${base()}/readiness/me`, scoped()),
  getIndividualReadiness: (email: string) =>
    axios.get<ReadinessResponse>(`${base()}/readiness/${encodeURIComponent(email)}`, scoped()),
  getDepartmentRollup: (department: string) =>
    axios.get<DepartmentAggregate>(`${base()}/department/${encodeURIComponent(department)}/rollup`, scoped()),
  getDashboard: () => axios.get<CompanyDashboard>(`${base()}/dashboard`, scoped()),
  getDashboardTrend: () => axios.get<DashboardTrend>(`${base()}/dashboard/trend`, scoped()),
  editProfileSummary: (summary: string) => axios.patch<{ ok: boolean; summary: string }>(`${base()}/profile`, { summary }, scoped()),
  getScoringConfig: () => axios.get<ScoringConfig>(`${base()}/scoring-config`, scoped()),
  updateScoringConfig: (body: { weights?: Record<string, number>; thresholds?: Record<string, number> }) =>
    axios.put<ScoringConfig>(`${base()}/scoring-config`, body, scoped()),
  getMembers: () => axios.get<RosterResponse>(`${base()}/members`, scoped()),

  // Role-based home dashboard + requests / approval queue / registry.
  getHome: () => axios.get<AaiHome>(`${base()}/home`, scoped()),
  // Every assigned client at once. Not scoped: the partner's dashboard is the
  // one read that spans companies rather than answering for the active one.
  getPartnerOverview: () => axios.get<PartnerOverview>(`${base()}/partner/overview`),
  getPartnerInbox: () => axios.get<{ events: ControlEvent[] }>(`${base()}/partner/inbox`),
  // The Owner's composed read: decisions waiting, counts, work, connections.
  getOwnerOverview: () => axios.get<OwnerOverview>(`${base()}/owner/overview`, scoped()),
  getAiControl: () => axios.get<AiControl>(`${base()}/owner/ai-control`, scoped()),
  getOwnerControls: () => axios.get<OwnerControls>(`${base()}/owner/controls`, scoped()),
  getOwnerAdoption: () => axios.get<OwnerAdoption>(`${base()}/owner/adoption`, scoped()),
  // The Department Head's composed read, scoped to the department they lead.
  getDepartmentOverview: () => axios.get<DepartmentOverview>(`${base()}/department/overview`, scoped()),
  requestDepartmentSupport: (body: { subject: string; note?: string; path?: string }) =>
    axios.post<ControlEvent>(`${base()}/department/support-request`, body, scoped()),
  listControlEvents: (kind?: string) =>
    axios.get<{ events: ControlEvent[] }>(`${base()}/owner/control-events`, { params: { ...(scoped()?.params || {}), ...compact({ kind }) } }),
  respondControlEvent: (id: string, body: { status: string; note?: string; slot_at?: string; slot_label?: string }, companyId?: string) => {
    let config = scoped();
    if (companyId) config = { params: { company_id: companyId } };
    return axios.post<ControlEvent>(`${base()}/owner/control-events/${encodeURIComponent(id)}/respond`, body, config);
  },
  // Platform staff: every shared blocker companies have routed to the platform.
  listPlatformBlockers: () => axios.get<{ events: ControlEvent[] }>(`${base()}/platform/blockers`),
  // The AI Champion's portfolio recommendation to the Owner and the Owner's answer.
  listPortfolioRecommendations: () =>
    axios.get<PortfolioRecommendationList>(`${base()}/portfolio/recommendations`, scoped()),
  sendPortfolioRecommendation: (body: { period: string; request_ids: string[]; recommended_limit: number; currency?: string; note?: string; sent_to?: string }) =>
    axios.post<PortfolioRecommendation>(`${base()}/portfolio/recommendations`, body, scoped()),
  respondPortfolioRecommendation: (id: string, body: { status: 'accepted' | 'adjusted' | 'declined'; final_limit?: number; note?: string }) =>
    axios.post<PortfolioRecommendation>(`${base()}/portfolio/recommendations/${encodeURIComponent(id)}/respond`, body, scoped()),
  recordControlEvent: (body: { kind: string; subject: string; status?: string; payload?: Record<string, unknown> }) =>
    axios.post<ControlEvent>(`${base()}/owner/control-events`, body, scoped()),
  // ── opportunities: the partner refines, the client decides ─────────────────
  listOpportunities: () => axios.get<{ opportunities: AaiOpportunity[] }>(`${base()}/opportunities`, scoped()),
  discoverOpportunities: () =>
    axios.post<{ created: number; skipped: number; opportunities: AaiOpportunity[] }>(`${base()}/opportunities/discover`, {}, scoped()),
  updateOpportunity: (id: string, body: {
    title?: string; partner_summary?: string; partner_notes?: string;
    review_state?: 'draft' | 'needs_review' | 'ready_for_client';
  }) => axios.patch<AaiOpportunity>(`${base()}/opportunities/${encodeURIComponent(id)}`, body, scoped()),
  requestOpportunityInfo: (id: string, question: string) =>
    axios.post<AaiOpportunity>(`${base()}/opportunities/${encodeURIComponent(id)}/request-info`, { question }, scoped()),
  sendOpportunity: (id: string) =>
    axios.post<AaiOpportunity>(`${base()}/opportunities/${encodeURIComponent(id)}/send`, {}, scoped()),
  // `assigned_elsewhere` is work named to you in another company (client work a
  // delivery expert owns). It is deliberately NOT in `requests`: that list is
  // the company's own work, which every rollup and approval queue counts.
  // Three keys, three questions. `requests` is this company's own work and what
  // every rollup counts; `assigned_elsewhere` is work named to you in another
  // company; `client_work` is every client's work and arrives only for a
  // provider Owner. Never merge them: a client's project in `requests` would be
  // somebody else's work inside our own numbers.
  listRequests: () => axios.get<{
    scope: string;
    requests: AaiRequest[];
    assigned_elsewhere?: AaiRequest[];
    client_work?: AaiRequest[];
  }>(`${base()}/requests`, scoped()),
  createRequest: (body: { title: string; detail?: string; risk?: string; capability_tag?: string; route?: 'quick_win' | 'project'; deliverable?: 'skill' | 'plugin' | 'document' | 'routine'; deliverable_reason?: string; tool_vendor?: string; source?: string }) =>
    axios.post<AaiRequest>(`${base()}/requests`, body, scoped()),
  decideRequest: (id: string, body: {
    decision: string; risk?: string; model?: string; human_in_loop?: string; comment?: string;
    // I2: name who will deliver it, at the gate.
    assigned_expert_email?: string;
  }) => axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/decision`, body, scoped()),
  // I2: the people who can be given a request (roster experts + provider staff).
  // ── routines (F1) ────────────────────────────────────────────────────────
  listRoutines: () =>
    axios.get<{ routines: AaiRoutine[]; cadences: string[] }>(`${base()}/my/routines`, scoped()),
  createRoutine: (body: {
    skill_id: string; cadence: string; time_of_day: string; weekday?: number; timezone?: string;
  }) => axios.post<AaiRoutine>(`${base()}/my/routines`, body, scoped()),
  pauseRoutine: (id: string, paused: boolean) =>
    axios.patch<AaiRoutine>(`${base()}/my/routines/${encodeURIComponent(id)}`, { paused }, scoped()),
  deleteRoutine: (id: string) =>
    axios.delete<{ deleted: boolean }>(`${base()}/my/routines/${encodeURIComponent(id)}`, scoped()),
  runRoutineNow: (id: string) =>
    axios.post<{ routine_id: string; outcome: string }>(
      `${base()}/my/routines/${encodeURIComponent(id)}/run`, {}, scoped()),

  assignableExperts: () =>
    axios.get<{ experts: { email: string; name: string }[] }>(`${base()}/requests/assignable-experts`, scoped()),
  assignRequestExpert: (id: string, email: string) =>
    axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/assign`, { email }, scoped()),
  // The roster: everyone on a request, and how a job is handed over. `assign`
  // above still names the primary builder and is unchanged.
  addRequestAssignee: (id: string, email: string, role: 'scoper' | 'builder') =>
    axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/roster`, { email, role }, scoped()),
  removeRequestAssignee: (id: string, email: string, role: 'scoper' | 'builder') =>
    axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/roster/remove`, { email, role }, scoped()),
  transferRequestAssignee: (id: string, body: { to_email: string; role: 'scoper' | 'builder'; from_email?: string }) =>
    axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/transfer`, body, scoped()),
  /* Where to carry on scoping this request, inside the Workspace. Returns the
     builder path to cross into; the conversation is per problem, so calling it
     twice reopens the same one rather than starting a rival. */
  /* Everything the caller can reach, across every project they can see. */
  getCompanyDocuments: () => axios.get<CompanyDocuments>(`${base()}/documents`, scoped()),
  /* The company's own library: for everyone, one department, or one person. */
  getDocumentLibrary: () => axios.get<CompanyDocumentLibrary>(`${base()}/documents/library`, scoped()),
  addLibraryDocument: (file: File, level: CompanyDocumentLevel, department = '') => {
    const form = new FormData();
    form.append('file', file);
    form.append('level', level);
    form.append('department', department);
    return axios.post<CompanyDocument>(`${base()}/documents/library`, form, {
      ...scoped(),
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadLibraryDocument: (id: string) =>
    axios.get<Blob>(`${base()}/documents/library/${encodeURIComponent(id)}/file`, { ...scoped(), responseType: 'blob' }),
  deleteLibraryDocument: (id: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/documents/library/${encodeURIComponent(id)}`, scoped()),
  /* One project's documents: uploaded into it, and produced by the work. */
  getProjectDocuments: (id: string) =>
    axios.get<ProjectDocuments>(`${base()}/requests/${encodeURIComponent(id)}/documents`, scoped()),
  scopeInWorkspace: (id: string) =>
    axios.post<{ path: string; conversation_id: string | null; project_id: string | null; created: boolean }>(
      `${base()}/requests/${encodeURIComponent(id)}/scope-in-workspace`, {}, scoped()),
  scopeRequest: (id: string, body: {
    owner_email?: string; target_phase?: string; capability_tag?: string; scope_notes?: string;
    eu_ai_act_class?: string; nist_function?: string; iso_control?: string; tool_vendor?: string;
    model?: string; human_in_loop?: string; data_flow?: string; est_cost_band?: string; est_value_band?: string;
  }) => axios.post<AaiRequest>(`${base()}/requests/${encodeURIComponent(id)}/scope`, body, scoped()),
  getRegistry: (params?: { department?: string; status?: string; capability?: string }) =>
    axios.get<{ entries: RegistryRow[]; scoped_department?: string | null }>(`${base()}/registry`, { params: { ...scoped()?.params, ...params } }),
  getDiscoveredTools: () => axios.get<{ discovered: DiscoveredTool[] }>(`${base()}/registry/discovered`, scoped()),
  logDiscoveredTools: (tools: string[]) =>
    axios.post<{ logged: string[] }>(`${base()}/registry/discovered/log`, { tools }, scoped()),
  getMarketplaceHint: (id: string) =>
    axios.get<{ eligible: boolean; match_count: number; top_match: string | null }>(
      `${base()}/requests/${encodeURIComponent(id)}/marketplace-hint`, scoped()),

  // Skills Marketplace (Feature 4).
  listSkills: () => axios.get<{ skills: AaiSkill[]; can_manage: boolean }>(`${base()}/skills`, scoped()),
  mySkills: () => axios.get<{ skills: AaiSkill[] }>(`${base()}/skills/mine`, scoped()),
  getSkill: (id: string) => axios.get<AaiSkill>(`${base()}/skills/${encodeURIComponent(id)}`, scoped()),
  createSkill: (body: Partial<AaiSkill>) => axios.post<AaiSkill>(`${base()}/skills`, body, scoped()),
  updateSkill: (id: string, body: Partial<AaiSkill>) => axios.patch<AaiSkill>(`${base()}/skills/${encodeURIComponent(id)}`, body, scoped()),
  publishSkill: (id: string, published: boolean) => axios.post<AaiSkill>(`${base()}/skills/${encodeURIComponent(id)}/publish`, { published }, scoped()),
  deleteSkill: (id: string) => axios.delete<{ ok: boolean }>(`${base()}/skills/${encodeURIComponent(id)}`, scoped()),
  requestSkillAccess: (id: string) => axios.post<{ status: string; auto: boolean }>(`${base()}/skills/${encodeURIComponent(id)}/request-access`, {}, scoped()),
  useSkill: (id: string) => axios.post<{ ok: boolean }>(`${base()}/skills/${encodeURIComponent(id)}/use`, {}, scoped()),
  skillAccessQueue: (id: string) => axios.get<{ access: SkillAccessRow[] }>(`${base()}/skills/${encodeURIComponent(id)}/access`, scoped()),
  runSkillTurn: (id: string, messages: Array<{ role: 'user' | 'assistant'; content: string }>, approvedTools: string[]) =>
    axios.post<{
      reply: string;
      gmail: { available: boolean; requires_approval: boolean; enabled: boolean; reason: string | null };
      needs_approval: string[];
      tool_activity: Array<{ tool: string; ok: boolean; query?: string | null }>;
    }>(`${base()}/my/skills/${encodeURIComponent(id)}/run-turn`, { messages, approved_tools: approvedTools }, scoped()),
  pendingSkillAccess: () =>
    axios.get<{ requests: Array<{ skill_id: string; skill_name: string; member_email: string; requested_at: string | null }> }>(
      `${base()}/skill-access-requests`, scoped()),
  decideSkillAccess: (id: string, email: string, grant: boolean) =>
    axios.post<{ ok: boolean }>(`${base()}/skills/${encodeURIComponent(id)}/access/${encodeURIComponent(email)}`, { grant }, scoped()),
  skillUsage: (id: string) => axios.get<SkillUsage>(`${base()}/skills/${encodeURIComponent(id)}/usage`, scoped()),

  // Vendor / Tool Evaluator (Feature 5).
  listVendorEvaluations: () => axios.get<{ evaluations: VendorEvaluation[] }>(`${base()}/vendor-evaluations`, scoped()),
  getVendorEvaluation: (id: string) => axios.get<VendorEvaluation>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}`, scoped()),
  createVendorEvaluation: (body: { title: string; framework_note?: string }) => axios.post<VendorEvaluation>(`${base()}/vendor-evaluations`, body, scoped()),
  updateVendorEvaluation: (id: string, body: { title?: string; framework_note?: string; recommendation?: string }) =>
    axios.patch<VendorEvaluation>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}`, body, scoped()),
  publishVendorEvaluation: (id: string, published: boolean) => axios.post<VendorEvaluation>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}/publish`, { published }, scoped()),
  deleteVendorEvaluation: (id: string) => axios.delete<{ ok: boolean }>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}`, scoped()),
  researchVendor: (id: string, name: string) => axios.post<VendorResearch>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}/research`, { name }, scoped()),
  addVendor: (id: string, body: Partial<VendorEntry> & { name: string }) => axios.post<VendorEntry>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}/vendors`, body, scoped()),
  updateVendor: (vid: string, body: Partial<VendorEntry>) => axios.patch<VendorEntry>(`${base()}/vendors/${encodeURIComponent(vid)}`, body, scoped()),
  deleteVendor: (vid: string) => axios.delete<{ ok: boolean }>(`${base()}/vendors/${encodeURIComponent(vid)}`, scoped()),
  setEvaluationPlaybook: (id: string, steps: { text: string; done: boolean }[]) =>
    axios.put<VendorEvaluation>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}/playbook`, { steps }, scoped()),
  announceEvaluation: (id: string) =>
    axios.post<VendorEvaluation>(`${base()}/vendor-evaluations/${encodeURIComponent(id)}/announce`, {}, scoped()),

  // Employee portal reads: Quick Wins, Results, and the activity history.
  // Results keep reviewed evidence and forecast apart, so the screen never has
  // to decide which of the two a number is.
  getMyQuickWins: () => axios.get<QuickWinList>(`${base()}/my/quick-wins`, scoped()),
  getMyResults: () => axios.get<MyResults>(`${base()}/my/results`, scoped()),
  getPendingReviews: () => axios.get<PendingReview[]>(`${base()}/my/results/pending`, scoped()),
  reviewRun: (executionId: string, body: { outcome: string; minutes_saved?: number | null; note?: string }) =>
    axios.post(`${base()}/my/results/${encodeURIComponent(executionId)}/review`, body, scoped()),
  getActivity: (scope: 'mine' | 'department' | 'company' = 'mine', limit = 50, filters: ActivityFilters = {}) =>
    axios.get<ActivityFeed>(`${base()}/activity`, {
      params: { ...(scoped()?.params || {}), scope, limit, ...compact({ ...filters }) },
    }),
  getActivityActors: () => axios.get<{ actors: string[] }>(`${base()}/activity/actors`, scoped()),
  getAccessHistory: (limit = 50) =>
    axios.get<{ events: ActivityEvent[] }>(`${base()}/activity/access`, {
      params: { ...(scoped()?.params || {}), limit },
    }),
  getMyAccess: () => axios.get<AccessMine>(`${base()}/activity/access/mine`, scoped()),
  getMyPreferences: () => axios.get<MyPreferencesResponse>(`${base()}/my/preferences`, scoped()),
  saveMyPreferences: (body: Partial<MemberPreferences>) =>
    axios.put<MemberPreferences>(`${base()}/my/preferences`, body, scoped()),

  // Insight sub-pages + program config.
  getProductivity: () => axios.get<ProductivityData>(`${base()}/my/productivity`, scoped()),
  getMyTools: () => axios.get<MyToolsData>(`${base()}/my/tools`, scoped()),
  getWhatsAllowed: () => axios.get<WhatsAllowedData>(`${base()}/whats-allowed`, scoped()),
  getHelp: () => axios.get<HelpData>(`${base()}/help`, scoped()),
  getGovernance: () => axios.get<GovernanceData>(`${base()}/governance`, scoped()),
  getReports: () => axios.get<ReportsData>(`${base()}/reports`, scoped()),
  // Orbit Control. Every call is company-scoped like the rest: a Solution
  // Partner prepares a collection for the client they are assigned to, and the
  // server re-authorizes that assignment on each one.
  // Read-only. `current` creates a draft when there is none, so a screen that
  // only reports on the cycle lists instead of asking for the current one.
  // Grants per company: defaults, overrides and what the caller may change.
  getGrants: (companyId?: string) =>
    axios.get<GrantsView>(`${base()}/grants`, { params: { ...(scoped()?.params || {}), ...(companyId ? { company_id: companyId } : {}) } }),
  setGrant: (role: string, permission: string, allowed: boolean | null, companyId?: string) =>
    axios.put<GrantsView>(`${base()}/grants/${encodeURIComponent(role)}/${encodeURIComponent(permission)}`, { allowed }, { params: { ...(scoped()?.params || {}), ...(companyId ? { company_id: companyId } : {}) } }),

  // Orbit Room: what Orbit knows and what it still needs, and the asks behind it.
  getOrbitRoom: (purpose: string, scope?: string) =>
    axios.get<OrbitRoomView>(`${base()}/orbit-room`, { params: { ...(scoped()?.params || {}), purpose, ...(scope ? { scope } : {}) } }),
  generateOrbitAsks: (purpose: string) =>
    axios.post<{ created: number }>(`${base()}/orbit-room/generate`, { purpose }, scoped()),
  getOrbitRoomSummary: () => axios.get<OrbitRoomSummary>(`${base()}/orbit-room/summary`, scoped()),
  /* The engagement board: the journey's phases with the conversations that
     happened in each. A read of things that already exist, not a second place
     work is tracked. */
  getEngagementBoard: () => axios.get<EngagementBoard>(`${base()}/orbit-room/board`, scoped()),
  /* Preparing the engagement: the arc an admin writes, or asks the copilot to
     draft, before a client walks it. */
  getEngagementTemplates: () =>
    axios.get<{ templates: EngagementTemplate[]; applied: AppliedEngagement | null; partner_default: PartnerJourneyDefault | null }>(
      `${base()}/engagement/templates`, scoped()),
  /* The order this partner's clients walk. Clients already under way keep the
     order they had. */
  /* Whether the partner runs on their own framework or ours, and how far its
     setup got. A client reads its partner's, read-only. */
  getFrameworkProfile: (clientId: string) =>
    axios.get<FrameworkProfile>(`${base()}/framework/profile`, { params: compact({ client_id: clientId }) }),
  saveFrameworkProfile: (body: { choice?: 'ours' | 'own'; status?: '' | 'setting_up' | 'live'; gaps?: FrameworkGap[] }) =>
    axios.put<FrameworkProfile>(`${base()}/framework/profile`, body),
  /* The order the intake tabs run in; saving it moves the journey to match. */
  /* Where a question from a document can go, as the framework stands now. */
  getFrameworkTargets: (clientId: string) =>
    axios.get<{ targets: FrameworkReadTarget[] }>(`${base()}/framework/targets`, { params: compact({ client_id: clientId }) }),
  getIntakeOrder: (clientId: string) =>
    axios.get<{ order: string[]; set_here: boolean; names?: Record<string, string>; deleted?: string[] }>(
      `${base()}/framework/intake-order`, { params: compact({ client_id: clientId }) }),
  /* Rename an intake of the partner's own framework, delete it, or bring it back. */
  /* Temporary: put the partner's framework back to the start. */
  resetFramework: () => axios.post<{ ok: boolean; changes: number }>(`${base()}/framework/reset`, {}),
  changeIntake: (intake: string, body: { label?: string; deleted?: boolean }) =>
    axios.put<{ names: Record<string, string>; deleted: string[] }>(
      `${base()}/framework/intakes/${encodeURIComponent(intake)}`, body),
  putIntakeOrder: (clientId: string, order: string[]) =>
    axios.put<{ order: string[]; set_here: boolean; kept: number }>(`${base()}/framework/intake-order`, { client_id: clientId, order }),
  /* Make these the opening questions, replacing the current set at this scope. */
  setFrameworkOpening: (clientId: string, keys: string[]) =>
    axios.put(`${base()}/framework/opening`, { client_id: clientId, keys }),
  setPartnerJourney: (body: { name: string; phases: ArcPhase[] }) =>
    axios.put<{ default: PartnerJourneyDefault | null; kept: number }>(`${base()}/engagement/default`, body, scoped()),
  clearPartnerJourney: () =>
    axios.delete<{ default: null; kept: number }>(`${base()}/engagement/default`, scoped()),
  proposeArc: (brief: string) =>
    axios.post<ProposedArc>(`${base()}/engagement/propose`, { brief }, scoped()),
  saveEngagementTemplate: (body: {
    name: string; blurb?: string; phases: ArcPhase[]; template_id?: string;
  }) => axios.post<{ template: EngagementTemplate }>(`${base()}/engagement/templates`, body, scoped()),
  prepareEngagement: (body: { phases: ArcPhase[]; template_id?: string; template_name?: string }) =>
    axios.post<{ applied: AppliedEngagement }>(`${base()}/engagement/prepare`, body, scoped()),
  getConversation: (id: string) =>
    axios.get<{ conversation: OrbitConversation }>(`${base()}/orbit-room/board/${id}`, scoped()),
  closeConversation: (id: string) =>
    axios.post<{ ok: boolean }>(`${base()}/orbit-room/board/${id}/close`, {}, scoped()),
  getOrbitRoomPeople: () => axios.get<{ people: OrbitRoomPerson[] }>(`${base()}/orbit-room/people`, scoped()),
  confirmOrbitFact: (body: { field_key: string; scope?: string }) =>
    axios.post<{ ok: boolean }>(`${base()}/orbit-room/facts/confirm`, body, scoped()),
  correctOrbitFact: (body: { field_key: string; scope?: string; value: unknown }) =>
    axios.post<{ ok: boolean }>(`${base()}/orbit-room/facts/correct`, body, scoped()),
  myOrbitAsks: () => axios.get<{ asks: OrbitAsk[]; onboarding_left: number }>(`${base()}/orbit-asks/mine`, scoped()),
  createOrbitAsk: (body: { purpose: string; bucket: string; scope?: string; question: string; why?: string; kind?: string; options?: { value: string; label: string }[]; responsible_email?: string; onboarding?: boolean }) =>
    axios.post<{ ask: OrbitAsk }>(`${base()}/orbit-asks`, body, scoped()),
  updateOrbitAsk: (id: string, body: { question?: string; why?: string; responsible_email?: string; onboarding?: boolean }) =>
    axios.patch<{ ask: OrbitAsk }>(`${base()}/orbit-asks/${encodeURIComponent(id)}`, body, scoped()),
  dropOrbitAsk: (id: string) =>
    axios.delete<{ ask: OrbitAsk }>(`${base()}/orbit-asks/${encodeURIComponent(id)}`, scoped()),
  delegateOrbitAsk: (id: string, body: { to_email: string; note?: string }) =>
    axios.post<{ ask: OrbitAsk }>(`${base()}/orbit-asks/${encodeURIComponent(id)}/delegate`, body, scoped()),
  answerOrbitAsk: (id: string, value: unknown) =>
    axios.post<{ ask: OrbitAsk }>(`${base()}/orbit-asks/${encodeURIComponent(id)}/answer`, { value }, scoped()),
  promoteOrbitAsk: (id: string, target: 'framework' | 'client') =>
    axios.post<{ ask: OrbitAsk; key: string; scope: string }>(`${base()}/orbit-asks/${encodeURIComponent(id)}/promote`, { target }, scoped()),
  skipOrbitAsk: (id: string) =>
    axios.post<{ ask: OrbitAsk }>(`${base()}/orbit-asks/${encodeURIComponent(id)}/skip`, {}, scoped()),

  // The deck and the Room's head.
  getOrbitDeck: (audience: 'me' | 'partner' = 'me') =>
    axios.get<OrbitDeckResponse>(`${base()}/orbit-room/deck`, { params: { ...(scoped()?.params || {}), audience } }),
  /* The pain point named on the sign-up deck, made a problem card at once. */
  createDeckProblem: (statement: string) =>
    axios.post<{ id: string }>(`${base()}/orbit-room/problem`, { statement }, scoped()),
  getOrbitOverview: () => axios.get<OrbitOverview>(`${base()}/orbit-room/overview`, scoped()),
  getOrbitTools: () => axios.get<OrbitToolBoard>(`${base()}/orbit-room/tools`, scoped()),
  /* A toolkit from Composio's catalogue, put on this company's board. */
  addCompanyTool: (body: { slug: string; label?: string; category_label?: string; logo?: string }) =>
    axios.post<{ ok: boolean; tool: { slug: string; label: string } }>(`${base()}/orbit-room/tools/add`, body, scoped()),
  removeCompanyTool: (toolKey: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/orbit-room/tools/${encodeURIComponent(toolKey)}`, scoped()),

  /* What each tool is good for, read beside the tool. */
  getToolOpportunities: (params: { tool_key?: string; department?: string; status?: string } = {}) =>
    axios.get<ToolOpportunityList>(`${base()}/orbit-room/tools/opportunities`, {
      ...(scoped() || {}),
      params: { ...((scoped() as any)?.params || {}), ...params },
    }),
  /* Go deeper on one tool: mark it dived and deal its own questions. */
  openToolDive: (body: { tool_key: string; tool_label?: string }) =>
    axios.post<{ tool_key: string; started: boolean; created: number }>(
      `${base()}/orbit-room/tools/dive`, body, scoped()),
  generateToolOpportunities: (body: { tool_key: string; tool_label?: string }) =>
    axios.post<{ created: number; source: string; opportunities: ToolOpportunity[] }>(
      `${base()}/orbit-room/tools/opportunities/generate`, body, scoped()),
  /* Marks it as being built; the workspace reads the opening message itself. */
  openToolOpportunityWorkspace: (id: string) =>
    axios.post<{ opportunity: ToolOpportunity; lane: string }>(
      `${base()}/orbit-room/tools/opportunities/${encodeURIComponent(id)}/workspace`, {}, scoped()),
  enableToolOpportunity: (id: string) =>
    axios.post<{ opportunity: ToolOpportunity }>(
      `${base()}/orbit-room/tools/opportunities/${encodeURIComponent(id)}/enable`, {}, scoped()),
  dismissToolOpportunity: (id: string) =>
    axios.post<{ opportunity: ToolOpportunity }>(
      `${base()}/orbit-room/tools/opportunities/${encodeURIComponent(id)}/dismiss`, {}, scoped()),
  getOrbitPhases: () => axios.get<OrbitJourney>(`${base()}/orbit-room/phases`, scoped()),
  markOrbitPhase: (phase: number, state: 'done' | 'open') =>
    axios.post<OrbitJourney>(`${base()}/orbit-room/phases/${phase}/mark`, { state }, scoped()),
  startOrbitPhase: (phase: number) =>
    axios.post<{ ok: boolean }>(`${base()}/orbit-room/phases/${phase}/start`, {}, scoped()),
  confirmOrbitFacts: (items: { field_key: string; scope?: string; correct?: boolean; skip?: boolean; value?: unknown }[]) =>
    axios.post<{ confirmed: number }>(`${base()}/orbit-room/facts/confirm-batch`, { items }, scoped()),
  getOrbitProposals: () => axios.get<{ proposals: OrbitProposal[] }>(`${base()}/orbit-room/proposals`, scoped()),
  decideOrbitProposals: (body: { accept: string[]; dismiss: string[] }) =>
    axios.post<{ accepted: { departments: number; people: number }; dismissed: number; proposals: OrbitProposal[] }>(`${base()}/orbit-room/proposals/decide`, body, scoped()),

  // The framework: which questions Orbit asks, per partner and per client.
  getFramework: (registry: string, clientId?: string) =>
    axios.get<FrameworkView>(`${base()}/framework`, { params: { registry, client_id: clientId || '' } }),
  putFramework: (registry: string, key: string, body: { op: string; spec?: Record<string, unknown>; client_id?: string }) =>
    axios.put<FrameworkView>(`${base()}/framework/${encodeURIComponent(registry)}/${encodeURIComponent(key)}`, body),
  /* Several at once, for taking what a document proposed. Partial by design:
     the answer names what landed and what the registry refused. */
  putFrameworkBulk: (registry: string, body: {
    client_id?: string;
    fields: { key: string; op?: string; spec?: Record<string, unknown> }[];
  }) => axios.post<FrameworkView & { added: string[]; refused: { key: string; reason: string }[] }>(
    `${base()}/framework/${encodeURIComponent(registry)}/bulk`, body),
  // `op` drops one facet only: 'starter' takes a question out of the opening
  // and leaves the question itself in the framework.
  deleteFramework: (registry: string, key: string, clientId?: string, op?: string) =>
    axios.delete<FrameworkView>(`${base()}/framework/${encodeURIComponent(registry)}/${encodeURIComponent(key)}`, { params: { client_id: clientId || '', ...(op ? { op } : {}) } }),
  getFrameworks: () => axios.get<{ frameworks: PartnerFramework[] }>(`${base()}/frameworks`),
  /* Intakes a partner defined for themselves. A custom intake is a registry
     name, so the four calls above work on one without knowing it is custom. */
  getIntakeTypes: (clientId?: string) =>
    axios.get<{ types: IntakeTypeRow[]; scope: string; limit: number }>(
      `${base()}/framework/types`, { params: { client_id: clientId || '' } }),
  createIntakeType: (body: { label: string; blurb?: string; client_id?: string }) =>
    axios.post<IntakeTypeRow>(`${base()}/framework/types`, body),
  renameIntakeType: (slug: string, body: { label?: string; blurb?: string; client_id?: string }) =>
    axios.put<IntakeTypeRow>(`${base()}/framework/types/${encodeURIComponent(slug)}`, body),
  archiveIntakeType: (slug: string, clientId?: string) =>
    axios.delete<{ archived: string }>(`${base()}/framework/types/${encodeURIComponent(slug)}`,
      { params: { client_id: clientId || '' } }),
  /* How deep Orbit works on this scope, and where that was decided. */
  getJourneyHint: (clientId?: string) =>
    axios.get<JourneyHint>(`${base()}/framework/journey`, { params: { client_id: clientId || '' } }),
  /* 'inherit' stops deciding here and follows the layer above. */
  setJourneyHint: (clientId: string, depth: string) =>
    axios.put<JourneyHint>(`${base()}/framework/journey`, { client_id: clientId, depth }),
  /* One document, read for both halves: the questions it says are worth asking
     and the answers it already holds. Nothing is written by this call. */
  readFrameworkDocument: (file: File, registry: string, clientId?: string) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return axios.post<FrameworkDocumentRead>(`${base()}/framework/read-document`, form, {
      params: { registry, client_id: clientId || '' },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /* The read as a job. The upload returns an id and nothing else: the answer is
     collected by polling, which is what lets a partner leave the screen while a
     folder of documents is still going through. */
  startFrameworkRead: (
    files: File[], registry: string, clientId?: string, section?: string, intake?: string,
  ) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    return axios.post<{ job_id: string; total: number; scope: string }>(
      `${base()}/framework/read-jobs`, form, {
        params: {
          registry, client_id: clientId || '', section: section || '', intake: intake || registry,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  },
  getFrameworkReadJob: (jobId: string) =>
    axios.get<FrameworkReadJob>(`${base()}/framework/read-jobs/${jobId}`),
  rereadFramework: (jobId: string, roles: Record<string, string>) =>
    axios.post<{ job_id: string }>(`${base()}/framework/read-jobs/${jobId}/reread`, { roles }),
  latestFrameworkReadJob: (registry: string, clientId?: string) =>
    axios.get<{ job: FrameworkReadJob | null }>(`${base()}/framework/read-jobs`, {
      params: { registry, client_id: clientId || '' },
    }),
  /* The person-first intake. The two public calls carry a signed partner code
     instead of a session: whoever scanned the QR has no account and is not
     about to make one. */
  personIntakeQuestions: (ref: string) =>
    axios.get<PersonIntakeQuestions>(`${getApiBaseUrl()}/public/person-intake`, { params: { ref } }),
  submitPersonIntake: (body: {
    ref: string; name: string; email?: string; job_title?: string;
    company_name?: string; answers: Record<string, unknown>;
  }) => axios.post<{ ok: boolean; id: string; returning: boolean }>(
    `${getApiBaseUrl()}/public/person-intake`, body),
  /* The session lane: one question at a time, chosen on the server. The wizard
     calls above still work and are what a client that cannot hold a session
     falls back to. */
  startPersonIntake: (body: { ref: string; name: string; email?: string; job_title?: string }) =>
    axios.post<PersonIntakeState>(`${getApiBaseUrl()}/public/person-intake/session`, body),
  readPersonIntakeSession: (ref: string, sessionId: string) =>
    axios.get<PersonIntakeState>(`${getApiBaseUrl()}/public/person-intake/session`, {
      params: { ref, session_id: sessionId },
    }),
  answerPersonIntake: (body: {
    ref: string; session_id: string; key: string; value?: unknown; skipped?: boolean;
  }) => axios.post<PersonIntakeState>(
    `${getApiBaseUrl()}/public/person-intake/session/answer`, body),
  finishPersonIntake: (body: { ref: string; session_id: string; company_name: string }) =>
    axios.post<{ ok: boolean; id: string; returning: boolean }>(
      `${getApiBaseUrl()}/public/person-intake/session/finish`, body),
  intakeSections: () => axios.get<IntakeSectionsView>(`${base()}/intake/sections`),
  addIntakeSection: (body: IntakeSectionBody) => axios.post<IntakeSectionsView>(`${base()}/intake/sections`, body),
  editIntakeSection: (id: string, body: IntakeSectionBody) =>
    axios.put<IntakeSectionsView>(`${base()}/intake/sections/${id}`, body),
  adjustOurIntakeSection: (id: string, body: { questions: IntakeSectionBody['questions']; removed: boolean }) =>
    axios.put<IntakeSectionsView>(`${base()}/intake/sections/${id}/ours`, body),
  resetOurIntakeSection: (id: string) => axios.delete<IntakeSectionsView>(`${base()}/intake/sections/${id}/ours`),
  deleteIntakeSection: (id: string) => axios.delete<IntakeSectionsView>(`${base()}/intake/sections/${id}`),
  moveIntakeSection: (id: string, direction: 'up' | 'down') =>
    axios.post<IntakeSectionsView>(`${base()}/intake/sections/${id}/move`, { direction }),
  intakePeople: (clientId: string) =>
    axios.get<{ people: Record<string, IntakePersonProgress> }>(`${base()}/intake/people`, { params: { company_id: clientId } }),
  intakeJourney: (clientId: string) =>
    axios.get<IntakeJourney>(`${base()}/intake/journey`, { params: { company_id: clientId } }),
  intakePerson: (clientId: string, email: string) =>
    axios.get<IntakePersonDetail>(`${base()}/intake/people/${encodeURIComponent(email)}`, { params: { company_id: clientId } }),
  myIntake: (clientId?: string) =>
    axios.get<{ sections: IntakeSection[]; filling?: boolean }>(`${base()}/intake/mine`, { params: compact({ company_id: clientId }) }),
  answerMyIntake: (sectionId: string, answers: Record<string, string>, clientId?: string, subject = '') =>
    axios.post<{ sections: IntakeSection[]; filling?: boolean }>(`${base()}/intake/mine/${sectionId}`, { answers, subject }, { params: compact({ company_id: clientId }) }),
  myPart: (clientId?: string) =>
    axios.get<MyPart>(`${base()}/orbit-room/my-part`, { params: compact({ company_id: clientId }) }),
  /* They worked through one step's cards on their part: that step is done. */
  finishMyPart: (step: string, clientId?: string) =>
    axios.post<MyPart>(`${base()}/orbit-room/my-part/finish`, { step }, { params: compact({ company_id: clientId }) }),
  answerMyPart: (body: { key: string; value?: unknown; skipped?: boolean }, clientId?: string) =>
    axios.post<ClientPerson>(`${base()}/orbit-room/my-part/answer`, body, { params: compact({ company_id: clientId }) }),
  readInvitedPersonIntake: (ref: string) =>
    axios.get<PersonIntakeState>(`${getApiBaseUrl()}/public/person-intake/invited`, { params: { ref } }),
  /* The Person intake step of a client's journey: people the partner added. */
  clientPeople: (clientId: string) =>
    axios.get<{ people: ClientPerson[]; roles: string[] }>(`${base()}/person-intake/clients/${clientId}/people`),
  addClientPerson: (clientId: string, body: { name: string; email: string; job_title?: string; role: string }) =>
    axios.post<ClientPerson>(`${base()}/person-intake/clients/${clientId}/people`, body),
  fillForPerson: (clientId: string, personId: string, key: string, value: unknown) =>
    axios.put<ClientPerson>(`${base()}/person-intake/clients/${clientId}/people/${personId}/answers`, { key, value }),
  setPersonRole: (clientId: string, personId: string, role: string) =>
    axios.put<ClientPerson>(`${base()}/person-intake/clients/${clientId}/people/${personId}/role`, { role }),
  personIntakeLink: (clientId: string, personId: string) =>
    axios.get<{ url: string }>(`${base()}/person-intake/clients/${clientId}/people/${personId}/link`),
  invitePerson: (clientId: string, personId: string) =>
    axios.post<{ sent: boolean; email: string; url: string }>(`${base()}/person-intake/clients/${clientId}/people/${personId}/invite`),
  joinClientPerson: (clientId: string, personId: string) =>
    axios.post<ClientPerson>(`${base()}/person-intake/clients/${clientId}/people/${personId}/join`),
  removeClientPerson: (clientId: string, personId: string) =>
    axios.delete(`${base()}/person-intake/clients/${clientId}/people/${personId}`),
  mintPersonIntakeLink: (expiresDays?: number, consultantId?: string) =>
    axios.post<PersonIntakeLink>(`${base()}/person-intake/link`, {
      expires_days: expiresDays || 30, consultant_id: consultantId || '',
    }),
  prospects: (consultantId?: string) =>
    axios.get<{ prospects: PersonProspect[] }>(`${base()}/prospects`, {
      params: { consultant_id: consultantId || '' },
    }),
  prospectRollup: (consultantId?: string) =>
    axios.get<{ companies: ProspectCompany[] }>(`${base()}/prospects/rollup`, {
      params: { consultant_id: consultantId || '' },
    }),
  /* Say which client these people work for. The step that turns somebody who
     answered as an individual into part of a company the partner looks after. */
  placeProspects: (prospectIds: string[], clientId: string, invite = false, consultantId?: string) =>
    axios.post<{
      ok: boolean; placed: number; invited: number;
      skipped_no_email: number; failed: string[];
    }>(`${base()}/prospects/place`, {
      prospect_ids: prospectIds, client_id: clientId, invite,
      consultant_id: consultantId || '',
    }),
  /* The client's own workspace library, read across the crossing. Reachable
     only with a client open: a workspace belongs to one company. */
  workspaceDocuments: (clientId?: string) =>
    axios.get<{ documents: WorkspaceDocument[]; reason?: string }>(
      `${base()}/framework/workspace-documents`, { params: { client_id: clientId || '' } }),
  startWorkspaceRead: (body: {
    client_id: string; registry: string; section?: string; intake?: string; document_ids: string[];
  }) => axios.post<{ job_id: string; total: number; scope: string; missed: number }>(
    `${base()}/framework/read-jobs/from-workspace`, body),
  frameworkDocuments: (clientId?: string) =>
    axios.get<{ documents: FrameworkStoredDocument[]; scope: string }>(
      `${base()}/framework/documents`, { params: { client_id: clientId || '' } }),
  /* Blobs rather than links: a plain href carries no Authorization header, and
     these routes are scoped to whoever is asking. */
  downloadFrameworkDocument: (docId: string, clientId?: string) =>
    axios.get<Blob>(`${base()}/framework/documents/${docId}`, {
      params: { client_id: clientId || '' }, responseType: 'blob',
    }),
  exportFrameworkDocuments: (clientId?: string) =>
    axios.get<Blob>(`${base()}/framework/documents/export`, {
      params: { client_id: clientId || '' }, responseType: 'blob',
    }),
  deleteFrameworkDocument: (docId: string, clientId?: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/framework/documents/${docId}`, {
      params: { client_id: clientId || '' },
    }),
  keepFrameworkValues: (clientId: string, values: FrameworkValueProposal[]) =>
    axios.post<{ written: number; held: number }>(`${base()}/framework/values`, { client_id: clientId, values }),
  getStarterDeck: (clientId?: string) =>
    axios.get<{ cards: StarterDeckCard[]; scope: string; limit: number }>(`${base()}/starter-deck`, { params: { client_id: clientId || '' } }),

  listOrbitCollections: () =>
    axios.get<{ collections: OrbitCollection[] }>(`${base()}/orbit-collections`, scoped()),
  getOrbitCollection: (id: string) =>
    axios.get<OrbitCollectionView>(`${base()}/orbit-collections/${encodeURIComponent(id)}`, scoped()),

  // The Solution Partner's own account, boundary, and app catalog here.
  getPartnerSettings: () => axios.get<PartnerSettingsData>(`${base()}/partner-settings`, scoped()),

  // The client's plan and the commercial state of their Delivery Projects.
  getPlanUsage: () => axios.get<PlanAndUsageData>(`${base()}/plan-usage`, scoped()),

  // The Delivery Project panel, its quote versions, and its delivery notes.
  getDeliveryProject: (id: string) =>
    axios.get<DeliveryProjectPanel>(`${base()}/projects/${encodeURIComponent(id)}`, scoped()),
  addDeliveryNote: (id: string, body: string) =>
    axios.post<{ note: DeliveryNote; notes: DeliveryNote[] }>(
      `${base()}/projects/${encodeURIComponent(id)}/notes`, { body }, scoped()),
  createDeliveryQuote: (id: string, body: Record<string, unknown>) =>
    axios.post<{ quote: DeliveryQuote; quotes: DeliveryQuote[] }>(
      `${base()}/projects/${encodeURIComponent(id)}/quote`, body, scoped()),
  updateDeliveryQuote: (id: string, quoteId: string, body: Record<string, unknown>) =>
    axios.patch<{ quote: DeliveryQuote; quotes: DeliveryQuote[] }>(
      `${base()}/projects/${encodeURIComponent(id)}/quote/${encodeURIComponent(quoteId)}`, body, scoped()),
  acceptDeliveryQuote: (id: string, quoteId: string) =>
    axios.post<{ quote: DeliveryQuote; quotes: DeliveryQuote[] }>(
      `${base()}/projects/${encodeURIComponent(id)}/quote/${encodeURIComponent(quoteId)}/accept`, {}, scoped()),
  sendDeliveryQuote: (id: string, quoteId: string) =>
    axios.post<{ quote: DeliveryQuote; quotes: DeliveryQuote[] }>(
      `${base()}/projects/${encodeURIComponent(id)}/quote/${encodeURIComponent(quoteId)}/send`, {}, scoped()),
  setDeliveryBuildState: (id: string, state: 'built' | 'tested') =>
    axios.post<{ project: DeliveryProject }>(
      `${base()}/projects/${encodeURIComponent(id)}/build-state`, { state }, scoped()),
  deliverProject: (id: string, body: Record<string, unknown>) =>
    axios.post<{ skill: AaiSkill; project: DeliveryProject }>(
      `${base()}/projects/${encodeURIComponent(id)}/deliver`, body, scoped()),

  getProgram: () => axios.get<ProgramConfig>(`${base()}/program`, scoped()),
  updateProgram: (body: Partial<Pick<ProgramConfig, 'enabled' | 'preset' | 'name' | 'phases' | 'tier' | 'starts_on'>>) =>
    axios.put<ProgramConfig>(`${base()}/program`, body, scoped()),
  listTemplates: () => axios.get<{ templates: ProgramTemplate[] }>(`${base()}/templates`, scoped()),
  deleteTemplate: (templateKey: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/templates/${encodeURIComponent(templateKey)}`, scoped()),
  applyTemplate: (clientId: string, body: { template_key: string }) =>
    axios.post<{ ok: boolean }>(`${base()}/clients/${encodeURIComponent(clientId)}/apply-template`, body, scoped()),

  // Trainer cockpit: curriculum + cohorts + sessions (manage_program).
  getCurriculum: () => axios.get<Curriculum>(`${base()}/curriculum`, scoped()),
  getGettingStarted: () => axios.get<GettingStarted>(`${base()}/curriculum/getting-started`, scoped()),
  setupProgram: (choice: 'aa_standard' | 'custom') =>
    axios.post<Curriculum>(`${base()}/curriculum/setup`, { choice }, scoped()),
  renameProgram: (name: string) =>
    axios.patch<Curriculum>(`${base()}/curriculum/program`, { name }, scoped()),
  setHandoff: (handedOff: boolean) =>
    axios.post<Curriculum>(`${base()}/curriculum/handoff`, { handed_off: handedOff }, scoped()),
  saveAsTemplate: (body: { name: string; description?: string }) =>
    axios.post<{ template_key: string }>(`${base()}/curriculum/save-as-template`, body, scoped()),
  patchPhase: (id: string, body: { title?: string; description?: string; status?: string; week_start?: number | null; week_end?: number | null }) =>
    axios.patch<Curriculum>(`${base()}/curriculum/phases/${encodeURIComponent(id)}`, body, scoped()),
  createPhase: (body: { title: string; description?: string }) =>
    axios.post<Curriculum>(`${base()}/curriculum/phases`, body, scoped()),
  reorderPhases: (orderedIds: string[]) =>
    axios.post<Curriculum>(`${base()}/curriculum/phases/reorder`, { ordered_ids: orderedIds }, scoped()),
  deletePhase: (id: string) =>
    axios.delete<Curriculum>(`${base()}/curriculum/phases/${encodeURIComponent(id)}`, scoped()),
  addModule: (phaseId: string, body: { title: string; description?: string; session_type?: string; sort_order?: number; content?: Record<string, unknown>; method?: string; delivery_mode?: string }) =>
    axios.post<{ id: string }>(`${base()}/curriculum/phases/${encodeURIComponent(phaseId)}/modules`, body, scoped()),
  reorderModules: (phaseId: string, orderedIds: string[]) =>
    axios.post<Curriculum>(`${base()}/curriculum/phases/${encodeURIComponent(phaseId)}/modules/reorder`, { ordered_ids: orderedIds }, scoped()),
  patchModule: (id: string, body: Record<string, unknown>) =>
    axios.patch<{ ok: boolean }>(`${base()}/curriculum/modules/${encodeURIComponent(id)}`, body, scoped()),
  deleteModule: (id: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/curriculum/modules/${encodeURIComponent(id)}`, scoped()),

  listCohorts: () => axios.get<{ cohorts: Cohort[] }>(`${base()}/cohorts`, scoped()),
  suggestCohorts: () => axios.get<{ suggestions: CohortSuggestion[] }>(`${base()}/cohorts/suggest`, scoped()),
  createCohort: (body: { name: string; cut_type?: string; criteria?: Record<string, unknown>; member_emails: string[] }) =>
    axios.post<{ id: string }>(`${base()}/cohorts`, body, scoped()),
  getCohort: (id: string) => axios.get<CohortDetail>(`${base()}/cohorts/${encodeURIComponent(id)}`, scoped()),
  deleteCohort: (id: string) => axios.delete<{ ok: boolean }>(`${base()}/cohorts/${encodeURIComponent(id)}`, scoped()),

  listSessions: () => axios.get<{ sessions: AaiSession[] }>(`${base()}/sessions`, scoped()),
  scheduleSession: (body: { cohort_id?: string; title: string; session_type?: string; scheduled_at?: string; duration_min?: number; module_id?: string }) =>
    axios.post<{ id: string }>(`${base()}/sessions`, body, scoped()),
  generateSessions: (cohortId: string) =>
    axios.post<{ created: number; skipped: number }>(`${base()}/cohorts/${encodeURIComponent(cohortId)}/generate-sessions`, {}, scoped()),
  getAttendance: (id: string) =>
    axios.get<{ attendance: { member_email: string; attended: boolean }[] }>(`${base()}/sessions/${encodeURIComponent(id)}/attendance`, scoped()),
  recordAttendance: (id: string, attendance: Record<string, boolean>) =>
    axios.post<{ ok: boolean }>(`${base()}/sessions/${encodeURIComponent(id)}/attendance`, { attendance }, scoped()),
  setDeliverable: (id: string, body: { deliverable_type: string; deliverable_ref?: string }) =>
    axios.post<{ ok: boolean }>(`${base()}/sessions/${encodeURIComponent(id)}/deliverable`, body, scoped()),

  getMySessions: () => axios.get<MySessions>(`${base()}/my/sessions`, scoped()),

  // Participant training (MCQ). Gated: modules only after readiness is recorded.
  getMyTraining: () => axios.get<MyTraining>(`${base()}/my/training`, scoped()),
  getQuiz: (moduleId: string) =>
    axios.get<Quiz>(`${base()}/my/training/${encodeURIComponent(moduleId)}`, scoped()),
  submitQuiz: (moduleId: string, answers: number[]) =>
    axios.post<QuizResult>(`${base()}/my/training/${encodeURIComponent(moduleId)}/submit`, { answers }, scoped()),
  completeStep: (moduleId: string, submission?: Record<string, unknown>) =>
    axios.post<{ status: string; passed: boolean }>(`${base()}/my/training/${encodeURIComponent(moduleId)}/complete`, submission ? { submission } : {}, scoped()),

  // Video library (Trainer Framework §3) — standard (AAI-wide) + client recordings.
  listVideos: () => axios.get<VideoLibrary>(`${base()}/videos`, scoped()),
  createVideo: (body: { title: string; url: string; scope?: 'client' | 'standard' }) =>
    axios.post<AaiVideo>(`${base()}/videos`, body, scoped()),
  downloadBaselinePdf: () =>
    axios.get(`${base()}/my/baseline.pdf`, { ...(scoped() || {}), responseType: 'blob' }),

  // DEL-2 (v1): a 20-minute calendar block for one lesson, as a plain .ics.
  downloadLessonIcs: (moduleId: string, when?: string) =>
    axios.get(`${base()}/my/training/${moduleId}/calendar.ics`, {
      ...(scoped() || {}), responseType: 'blob',
      params: { ...((scoped() as any)?.params || {}), ...(when ? { when } : {}) },
    }),

  // DEL-1: Orbit invites each participant to their next lesson (manage_program).
  inviteParticipants: (cohortId?: string) =>
    axios.post<{ invited: number; skipped: number }>(`${base()}/training/invite`, { cohort_id: cohortId ?? null }, scoped()),

  // SKL-4: near matches from the marketplace for what's about to be built.
  suggestSkills: (q: string) =>
    axios.get<{ matches: Array<{ id: string; name: string; problem: string | null; process_steps: string[]; created_by: string | null; match: number }> }>(
      `${base()}/my/skill-builder/suggest`, { ...(scoped() || {}), params: { ...((scoped() as any)?.params || {}), q } }),

  // ROL-3: HR / IT intake (manage_program or manage_departments).
  getOrgIntake: () => axios.get(`${base()}/org/intake`, scoped()),
  // Add-time gate for the free-text tool input (same check the save enforces).
  checkTool: (tool: string) =>
    axios.get<{ tool: string; connectable: boolean }>(`${base()}/org/tool-check`, { ...(scoped() || {}), params: { ...((scoped() as any)?.params || {}), tool } }),

  // The IT picker's suggestions: evaluator-cleared + team-reported tools.
  // `connectable` = a known connector exists (badge, never a filter).
  getToolSuggestions: () =>
    axios.get<{ cleared: Array<{ tool: string; connectable?: boolean }>; in_use: Array<{ tool: string; users: number; connectable?: boolean }> }>(`${base()}/org/tool-suggestions`, scoped()),
  setHrIntake: (trainingBudget: string) =>
    axios.put(`${base()}/org/intake/hr`, { training_budget: trainingBudget }, scoped()),
  setItIntake: (toolStack: string[], noDestructive?: boolean) =>
    axios.put(`${base()}/org/intake/it`, { tool_stack: toolStack, no_destructive: noDestructive ?? null }, scoped()),

  // TD-1/2: the delegation walk — a tool's real capability list (risk-classified
  // by us), IT's blocks applied, the caller's answers attached.
  /* One tool: its actions with your decisions, and the skills that need it. */
  /* Declare which tools a skill needs. The whole list, not a delta. */
  setSkillTools: (id: string, tools: string[]) =>
    axios.put<{ skill_id: string; tools: string[] }>(
      `${base()}/skills/${encodeURIComponent(id)}/tools`, { tools }, scoped()),
  getToolControl: (tool: string) =>
    axios.get<ToolControl>(`${base()}/my/tools/${encodeURIComponent(tool)}/control`, scoped()),
  getDelegation: (tool: string) =>
    axios.get<ToolDelegation>(`${base()}/my/tool-dives/delegation`, { ...(scoped() || {}), params: { ...((scoped() as any)?.params || {}), tool } }),
  recordDelegation: (toolName: string, actionKey: string, decision: 'yes' | 'approval' | 'no') =>
    axios.post<ToolDelegation>(`${base()}/my/tool-dives/delegation`, { tool_name: toolName, action_key: actionKey, decision }, scoped()),

  // TD-3: the profile every skill inherits.
  getPermissionProfile: () => axios.get<PermissionProfile>(`${base()}/my/permission-profile`, scoped()),

  // ── Connectors ────────────────────────────────────────────────────────────
  // One connector API for the whole app. The caller's own connection state and
  // the organization's shared one come back as separate fields, so the UI can
  // never render a colleague's personal account as if it were the workspace's.
  getConnectors: () => axios.get<ConnectorCatalog>(`${base()}/connectors`, scoped()),
  getCodeHosts: (companyId: string) =>
    axios.get<CodeHostStatus>(`${base()}/connectors/code-hosts`, { params: { company_id: companyId } }),
  getCompanyToolPolicy: (provider: string) =>
    axios.get<CompanyToolPolicy>(`${base()}/connectors/actions`, {
      ...(scoped() || {}),
      params: { ...((scoped() as any)?.params || {}), provider },
    }),
  setCompanyActionLevel: (provider: string, actionKey: string, level: CompanyActionLevel) =>
    axios.put<CompanyToolPolicy>(`${base()}/connectors/actions`, { provider, action_key: actionKey, level }, scoped()),
  searchTools: (q: string, limit?: number) => axios.get<{ tools: { slug: string; label: string; logo: string; source: string }[] }>(`${base()}/connectors/search`, {
    ...(scoped() || {}),
    params: { ...((scoped() as any)?.params || {}), q, limit },
  }),
  getConnectorStatus: (provider: string, scope: ConnectorScope = 'personal') =>
    axios.get<ConnectorStatus>(`${base()}/connectors/status`, {
      ...(scoped() || {}),
      params: { ...((scoped() as any)?.params || {}), provider, scope },
    }),
  /* A tool that needs details at connect time (a Jira site, a Shopify store
     and token) asks for them here, so every Connect button gets the form. */
  initiateConnector: async (provider: string, scope: ConnectorScope = 'personal') => {
    const { data } = await axios.get<ConnectFieldsResponse>(`${base()}/connectors/fields`, {
      ...(scoped() || {}),
      params: { ...((scoped() as any)?.params || {}), provider, scope },
    });
    const label = provider.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const cancelled = () => Object.assign(new Error('Connection cancelled.'), { response: { data: { detail: 'Connection cancelled.' } } });
    let needed = data.fields;
    /* A tool with no managed login and no sign-in that sets itself up needs
       an OAuth app of the organization's own, given once by an admin. */
    if (data.setup) {
      if (!data.setup.canSetUp) {
        const detail = `${label} needs a one-time sign-in setup. Ask an admin to connect it first.`;
        throw Object.assign(new Error(detail), { response: { data: { detail } } });
      }
      let note: string | undefined;
      if (data.setup.redirectUri) note = data.setup.redirectUri;
      let intro = `Create an app in ${label}'s developer settings and paste its details. This is done once for your organization.`;
      if (note) intro = `Create an app in ${label}'s developer settings, add this redirect URL to it, then paste its details. This is done once for your organization.`;
      const credentials = await askConnectFields(label, data.setup.fields, {
        title: `Set up ${label} sign-in`,
        intro,
        note,
        submit: 'Save and continue',
      });
      if (!credentials) throw cancelled();
      const setUp = await axios.post<{ fields: ConnectField[] }>(
        `${base()}/connectors/setup`, { provider, credentials }, scoped());
      needed = setUp.data.fields;
    }
    let fields: Record<string, string> = {};
    if (needed.length) {
      const given = await askConnectFields(label, needed);
      if (!given) throw cancelled();
      fields = given;
    }
    return axios.post<{ redirectUrl: string; alreadyConnected: boolean; connectedNow?: boolean; scope: ConnectorScope }>(
      `${base()}/connectors/initiate`, { provider, scope, fields }, scoped());
  },
  revokeConnector: (provider: string, scope: ConnectorScope = 'personal') =>
    axios.post<{ revoked: boolean; scope: ConnectorScope }>(
      `${base()}/connectors/revoke`, { provider, scope }, scoped()),
  getOrganizationConnections: () =>
    axios.get<OrganizationConnections>(`${base()}/connectors/organization`, scoped()),
  setConnectorEnabled: (provider: string, enabled: boolean) =>
    axios.post<{ provider: string; enabled: boolean; disabled: string[] }>(
      `${base()}/connectors/policy`, { provider, enabled }, scoped()),
  /* An app not on the company's list yet (a Composio search hit) joins it
     first, so its name and logo ride along. */
  setAppAccess: (provider: string, access: AppAccessMode, app: { label?: string; logo?: string } = {}) =>
    axios.put<{ provider: string; access: { mode: AppAccessMode } }>(
      `${base()}/connectors/access`, { provider, access, label: app.label || '', logo: app.logo || '' }, scoped()),

  // MCP servers in the company catalog, and the platform list behind them.
  listMcp: () => axios.get<McpView>(`${base()}/mcp`, scoped()),
  addMcp: (body: McpAddBody) => axios.post<McpServer>(`${base()}/mcp`, body, scoped()),
  addSuggestedMcp: (slug: string, secret?: string) =>
    axios.post<McpServer>(`${base()}/mcp/suggested/${encodeURIComponent(slug)}`, secret ? { secret } : undefined, scoped()),
  connectMcp: (key: string, secret: string) =>
    axios.post<McpServer>(`${base()}/mcp/${encodeURIComponent(key)}/connect`, { secret }, scoped()),
  disconnectMcp: (key: string) => axios.post<McpServer>(`${base()}/mcp/${encodeURIComponent(key)}/disconnect`, {}, scoped()),
  setMcpWriteLevel: (key: string, level: McpWriteLevel) =>
    axios.put<McpServer>(`${base()}/mcp/${encodeURIComponent(key)}/write-level`, { level }, scoped()),
  removeMcp: (key: string) => axios.delete<{ ok: boolean }>(`${base()}/mcp/${encodeURIComponent(key)}`, scoped()),
  beginMcpOauth: (key: string) => axios.post<{ authorizeUrl: string }>(`${base()}/mcp/${encodeURIComponent(key)}/oauth/begin`, {}, scoped()),
  beginMyMcpOauth: (key: string) => axios.post<{ authorizeUrl: string }>(`${base()}/mcp/mine/${encodeURIComponent(key)}/oauth/begin`, {}, scoped()),
  listMyMcp: () => axios.get<MyMcpView>(`${base()}/mcp/mine`, scoped()),
  getMcpPermissions: (key: string) =>
    axios.get<McpPermissionsView>(`${base()}/mcp/${encodeURIComponent(key)}/permissions`, scoped()),
  setMcpPermission: (key: string, body: { tool: string; decision: McpToolDecision | null; title?: string; read_only?: boolean }) =>
    axios.put<{ key: string; tool: string; decision: McpToolDecision | null }>(`${base()}/mcp/${encodeURIComponent(key)}/permissions`, body, scoped()),
  addMyMcp: (body: Omit<McpAddBody, 'departments'>) => axios.post<PersonalMcpServer>(`${base()}/mcp/mine`, body, scoped()),
  removeMyMcp: (key: string) => axios.delete<{ ok: boolean }>(`${base()}/mcp/mine/${encodeURIComponent(key)}`, scoped()),
  listPlatformMcp: () => axios.get<{ servers: McpCatalogEntry[] }>(`${base()}/admin/mcp-catalog`),
  addPlatformMcp: (body: Omit<McpAddBody, 'secret' | 'departments'>) =>
    axios.post<McpCatalogEntry>(`${base()}/admin/mcp-catalog`, body),
  removePlatformMcp: (slug: string) => axios.delete<{ ok: boolean }>(`${base()}/admin/mcp-catalog/${encodeURIComponent(slug)}`),

  // Per-person tool connections — the dive's "set it up" step made real.
  // Scoped to (company, member): one person's grant is theirs alone.
  getMyConnection: (tool: string) =>
    axios.get<{ available: boolean; connected: boolean; status?: string; organization_shared?: boolean }>(`${base()}/my/connections`, { ...(scoped() || {}), params: { ...((scoped() as any)?.params || {}), tool } }),
  initiateMyConnection: (tool: string) =>
    axios.post<{ redirectUrl: string; alreadyConnected: boolean }>(`${base()}/my/connections/initiate`, { tool }, scoped()),

  // TD-4: participant-safe read of the company tool stack + ground rule.
  getOrgToolStack: () =>
    axios.get<{ tool_stack: string[]; no_destructive: boolean }>(`${base()}/org/tool-stack`, scoped()),

  // TD-4: the same catalog + org rules, for What's Allowed.
  getToolActions: (tool: string) =>
    axios.get<ToolDelegation & { rules: { no_destructive: boolean } }>(`${base()}/tools/actions`, { ...(scoped() || {}), params: { ...((scoped() as any)?.params || {}), tool } }),

  // Org AI policy (Module 1 acceptable-use step). GET: any enrolled member; PUT: managers.
  getPolicy: () => axios.get<AaiPolicy>(`${base()}/policy`, scoped()),
  setPolicy: (body: string) => axios.put<AaiPolicy>(`${base()}/policy`, { body }, scoped()),

  // Module 3/4: guided skill builder (personal, self-only) + vetting loop.
  getSkillPrefill: (mode?: 'context' | 'workflow') =>
    axios.get<SkillPrefill>(`${base()}/my/skill-builder/prefill`, { params: { ...scoped()?.params, ...(mode ? { mode } : {}) } }),
  getMySkills: () => axios.get<{ skills: PersonalSkill[] }>(`${base()}/my/skills`, scoped()),
  // Workflow skills for the vetting section — includes graduated (shared) ones, so a
  // vetted skill stays visible to its author after it graduates out of personal scope.
  getMyWorkflowSkills: () => axios.get<{ skills: PersonalSkill[] }>(`${base()}/my/workflow-skills`, scoped()),
  createMySkill: (body: { name: string; problem?: string; process_steps: string[]; example_input?: string; example_output?: string; kind?: string }) =>
    axios.post<PersonalSkill>(`${base()}/my/skills`, body, scoped()),
  updateMySkill: (id: string, body: { name?: string; problem?: string; process_steps?: string[]; example_input?: string; example_output?: string; note?: string }) =>
    axios.patch<PersonalSkill>(`${base()}/my/skills/${encodeURIComponent(id)}`, body, scoped()),
  deleteMySkill: (id: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/my/skills/${encodeURIComponent(id)}`, scoped()),
  logSkillRun: (id: string, outcome: string, note?: string) =>
    axios.post<SkillRun>(`${base()}/my/skills/${encodeURIComponent(id)}/runs`, { outcome, note }, scoped()),
  getSkillVetting: (id: string) =>
    axios.get<VettingData>(`${base()}/my/skills/${encodeURIComponent(id)}/vetting`, scoped()),

  // Module 5: graduation (consent-first sharing) + org chart + mentorship.
  proposeGraduation: (id: string, toScope: 'team' | 'company' = 'team') =>
    axios.post<Graduation>(`${base()}/my/skills/${encodeURIComponent(id)}/graduate`, { to_scope: toScope }, scoped()),
  getMyGraduations: () => axios.get<{ graduations: Graduation[]; sharing_unlocked: boolean }>(`${base()}/my/graduations`, scoped()),
  getGraduationQueue: () => axios.get<{ graduations: Graduation[] }>(`${base()}/graduations`, scoped()),
  decideGraduation: (gradId: string, approve: boolean, note?: string) =>
    axios.post<Graduation>(`${base()}/graduations/${encodeURIComponent(gradId)}/decide`, { approve, note }, scoped()),
  getTeamBundles: () => axios.get<{ bundles: TeamBundle[] }>(`${base()}/team-bundles`, scoped()),
  getOrgChart: () => axios.get<OrgChartData>(`${base()}/org-chart`, scoped()),
  listMentorships: () => axios.get<{ mentorships: Mentorship[] }>(`${base()}/mentorships`, scoped()),
  assignMentorship: (coachEmail: string, learnerEmail: string) =>
    axios.post<Mentorship>(`${base()}/mentorships`, { coach_email: coachEmail, learner_email: learnerEmail }, scoped()),
  endMentorship: (id: string) => axios.delete<{ ok: boolean }>(`${base()}/mentorships/${encodeURIComponent(id)}`, scoped()),

  // Module 6: leadership impact dashboard + AA certification.
  getImpact: () => axios.get<ImpactData>(`${base()}/impact`, scoped()),
  getMyCertifications: () => axios.get<{ certifications: Certification[] }>(`${base()}/my/certifications`, scoped()),

  // Module 2: tool library (browse, no research spend) + tool dives (tracker).
  getToolLibrary: () => axios.get<{ tools: ToolLibraryEntry[] }>(`${base()}/tools/library`, scoped()),
  getToolDives: () => axios.get<DiveTrackerData>(`${base()}/my/tool-dives`, scoped()),
  startToolDive: (toolName: string, weekNo?: number) =>
    axios.post<ToolDive>(`${base()}/my/tool-dives`, { tool_name: toolName, week_no: weekNo }, scoped()),
  updateToolDive: (id: string, body: { steps?: DiveStep[]; notes?: string; status?: string }) =>
    axios.patch<ToolDive>(`${base()}/my/tool-dives/${encodeURIComponent(id)}`, body, scoped()),
  runDiveTurn: (id: string, messages: Array<{ role: 'user' | 'assistant'; content: string }>, approvedActions: string[]) =>
    axios.post<DiveTurnResult>(`${base()}/my/tool-dives/${encodeURIComponent(id)}/try-turn`,
      { messages, approved_actions: approvedActions }, scoped()),

  // First-class departments (Phase 1b).
  listDepartments: (ownCompany?: boolean) => axios.get<{ departments: AaiDepartment[] }>(`${base()}/departments`, own(ownCompany)),
  createDepartment: (body: { name: string; lead_email?: string; parent_id?: string; allow_no_head?: boolean }) =>
    axios.post<AaiDepartment>(`${base()}/departments`, body, scoped()),
  updateDepartment: (id: string, body: { name?: string; lead_email?: string; parent_id?: string }) =>
    axios.patch<{ departments: AaiDepartment[] }>(`${base()}/departments/${encodeURIComponent(id)}`, body, scoped()),
  deleteDepartment: (id: string) =>
    axios.delete<{ ok: boolean }>(`${base()}/departments/${encodeURIComponent(id)}`, scoped()),
  // Owner override — reassign a member to a department.
  setMemberDepartment: (email: string, departmentId: string) =>
    axios.patch<{ departments: AaiDepartment[] }>(
      `${base()}/members/${encodeURIComponent(email)}/department`, { department_id: departmentId }, scoped()),

  // Awaiting-approval count for the dashboard nav badge (F2 P0).
  approvalsSummary: (ownCompany?: boolean) =>
    axios.get<{ count: number }>(`${base()}/approvals/summary`, own(ownCompany)),
  // Server-generated branded adoption report PDF (F2 P1).
  downloadReportPdf: () =>
    axios.get(`${base()}/dashboard/report.pdf`, { ...(scoped() || {}), responseType: 'blob' }),

  // In-app notifications (shared delivery path — Registry + Marketplace).
  listNotifications: (params?: { unread_only?: boolean; limit?: number; all_clients?: boolean }) =>
    axios.get<AaiNotificationList>(`${base()}/notifications`, { params: { ...scoped()?.params, ...params } }),
  notificationsSummary: (allClients?: boolean) =>
    axios.get<{ unread_count: number }>(`${base()}/notifications/summary`, { params: { ...scoped()?.params, all_clients: allClients } }),
  markNotificationsRead: (ids?: string[], allClients?: boolean) =>
    axios.post<{ marked: number; unread_count: number }>(`${base()}/notifications/read`, { ids: ids ?? null }, { params: { ...scoped()?.params, all_clients: allClients } }),

  // PRD reviews (Expert role — the engineer gate before builds).
  listPrdReviews: () => axios.get<PrdReviewList>(`${base()}/prd-reviews`, scoped()),
  getPrdReview: (engagementId: string) =>
    axios.get<PrdReviewDetail>(`${base()}/prd-reviews/${engagementId}`, scoped()),
  decidePrdReview: (engagementId: string, body: { decision: 'approve' | 'request_changes'; comment?: string }) =>
    axios.post<{ ok: boolean; review: PrdReviewState }>(`${base()}/prd-reviews/${engagementId}/decision`, body, scoped()),
};
